import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import {
  formatDate as formatDateUtil,
  nowLocalDatetime,
  todayStr,
} from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
  manager_name: string | null
}

interface EntryRevision {
  id: number
  entry_type: number
  entry_text: string | null
  user_name: string | null
  created_at: string | null
}

interface Entry {
  id: number
  entry_type: number
  source_id: number | null
  start_time: string | null
  entry_text: string | null
  user_name: string | null
  also_names: string[]
  evidence_type_ids: number[]
  has_photo: boolean
  has_video: boolean
  status: number
  revisions: EntryRevision[]
}

interface EvidenceTypeOption {
  id: number
  name: string
  media_kind: string | null
  sort_order: number
}

interface TeamMember {
  id: number
  user_name: string | null
}

interface UserOption {
  id: number
  name: string
}

interface ReportDetail {
  id: number
  report_type: number
  category: number
  report_date: string | null
  notes: string | null
  admin_notes: string | null
  investigator_name: string | null
  user_name: string | null
  status: number
  approved_by_name: string | null
  approved_at: string | null
  prepared_by_name: string | null
  prepared_at: string | null
  used_in_final: number | null
  members: { id: number; user_name: string | null }[]
}

interface Props {
  case_record: CaseRecord
  report: ReportDetail
  original_entries: Entry[]
  final_entries: Entry[]
  has_finals: boolean
  team_members: TeamMember[]
  users: UserOption[]
  evidence_types: EvidenceTypeOption[]
  can_edit_entries: boolean
}

function formatDate(dateStr: string | null): string {
  return formatDateUtil(dateStr, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  })
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return ""
  return new Date(dateStr).toLocaleString()
}

function formatTime(dateStr: string | null): string {
  if (!dateStr) return ""
  return new Date(dateStr).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
}

const STATUS_LABELS: Record<number, string> = {
  0: "Draft",
  1: "Submitted",
  2: "Approved",
  3: "Prepared",
}

function EvidenceBadges({
  entry,
}: {
  entry: Pick<Entry, "has_photo" | "has_video">
}) {
  return (
    <>
      {entry.has_photo && (
        <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs font-semibold text-green-700">
          P
        </span>
      )}
      {entry.has_video && (
        <span className="rounded bg-purple-100 px-1.5 py-0.5 text-xs font-semibold text-purple-700">
          V
        </span>
      )}
    </>
  )
}

interface NewEntryForm {
  start_time: string
  entry_text: string
  attributed_user_id: string
  evidence_type_ids: number[]
}

export default function Prepare({
  case_record,
  report,
  original_entries,
  final_entries,
  has_finals,
  team_members,
  users,
  evidence_types,
  can_edit_entries,
}: Props) {
  const defaultEntryTime = () => {
    if (!report.report_date) return nowLocalDatetime()
    const reportDateStr = report.report_date.slice(0, 10)
    return todayStr() === reportDateStr
      ? nowLocalDatetime()
      : `${reportDateStr}T08:00`
  }

  const [selectedEntries, setSelectedEntries] = useState<number[]>(
    original_entries.map((e) => e.id),
  )
  const [includeInFinal, setIncludeInFinal] = useState(
    report.used_in_final === 1,
  )
  const [processing, setProcessing] = useState(false)
  const [showAddEntry, setShowAddEntry] = useState(false)
  const [editingEntryId, setEditingEntryId] = useState<number | null>(null)
  const [editForm, setEditForm] = useState<NewEntryForm>({
    start_time: "",
    entry_text: "",
    attributed_user_id: "",
    evidence_type_ids: [],
  })
  const [newEntry, setNewEntry] = useState<NewEntryForm>({
    start_time: defaultEntryTime(),
    entry_text: "",
    attributed_user_id: users[0]?.id?.toString() ?? "",
    evidence_type_ids: [],
  })

  const toggleEditFormType = (id: number) => {
    setEditForm((prev) => ({
      ...prev,
      evidence_type_ids: prev.evidence_type_ids.includes(id)
        ? prev.evidence_type_ids.filter((x) => x !== id)
        : [...prev.evidence_type_ids, id],
    }))
  }

  const toggleNewEntryType = (id: number) => {
    setNewEntry((prev) => ({
      ...prev,
      evidence_type_ids: prev.evidence_type_ids.includes(id)
        ? prev.evidence_type_ids.filter((x) => x !== id)
        : [...prev.evidence_type_ids, id],
    }))
  }

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Reports", href: `/cases/${case_record.id}/reports` },
    {
      title: `Report #${report.id}`,
      href: `/cases/${case_record.id}/reports/${report.id}`,
    },
    {
      title: "Prepare",
      href: `/cases/${case_record.id}/reports/${report.id}/prepare`,
    },
  ]

  const basePath = `/cases/${case_record.id}/reports`

  const toggleEntry = (entryId: number) => {
    setSelectedEntries((prev) =>
      prev.includes(entryId)
        ? prev.filter((id) => id !== entryId)
        : [...prev, entryId],
    )
  }

  const allEntryIds = original_entries.flatMap((e) => [
    e.id,
    ...e.revisions.map((r) => r.id),
  ])

  const toggleAll = () => {
    if (selectedEntries.length === allEntryIds.length) {
      setSelectedEntries([])
    } else {
      setSelectedEntries(allEntryIds)
    }
  }

  const handleBuildPrepared = () => {
    if (selectedEntries.length === 0) {
      alert(
        "Please select at least one entry to include in the prepared report.",
      )
      return
    }
    if (
      !confirm(
        "Build prepared report from selected entries? This will create final versions of the selected entries.",
      )
    ) {
      return
    }
    setProcessing(true)
    router.post(
      `${basePath}/${report.id}/entries/build_finals`,
      { entry_ids: selectedEntries, include_in_final: includeInFinal ? 1 : 0 },
      {
        onFinish: () => setProcessing(false),
      },
    )
  }

  const handleToggleFinalInclusion = () => {
    router.patch(
      `${basePath}/${report.id}/toggle_final_inclusion`,
      {},
      {
        preserveScroll: true,
      },
    )
  }

  const handleMarkPrepared = () => {
    if (!confirm("Mark this report as prepared and ready for the client?")) {
      return
    }
    setProcessing(true)
    router.patch(
      `${basePath}/${report.id}/mark_prepared`,
      {},
      {
        onFinish: () => setProcessing(false),
      },
    )
  }

  const startEditing = (entry: Entry) => {
    setEditingEntryId(entry.id)
    setEditForm({
      start_time: entry.start_time ? entry.start_time.slice(0, 19) : "",
      entry_text: entry.entry_text ?? "",
      attributed_user_id: "",
      evidence_type_ids: entry.evidence_type_ids ?? [],
    })
  }

  const handleUpdateEntry = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editForm.entry_text.trim()) {
      alert("Entry text is required.")
      return
    }
    setProcessing(true)
    router.patch(
      `${basePath}/${report.id}/update_prepare_entry`,
      {
        entry_id: editingEntryId,
        start_time: editForm.start_time,
        entry_text: editForm.entry_text,
        evidence_type_ids: editForm.evidence_type_ids,
      },
      {
        onFinish: () => {
          setProcessing(false)
          setEditingEntryId(null)
        },
      },
    )
  }

  const handleAddEntry = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newEntry.entry_text.trim()) {
      alert("Entry text is required.")
      return
    }
    setProcessing(true)
    router.post(
      `${basePath}/${report.id}/create_prepare_entry`,
      {
        start_time: newEntry.start_time,
        entry_text: newEntry.entry_text,
        attributed_user_id: newEntry.attributed_user_id,
        evidence_type_ids: newEntry.evidence_type_ids,
      },
      {
        onFinish: () => {
          setProcessing(false)
          setShowAddEntry(false)
          setNewEntry({
            start_time: defaultEntryTime(),
            entry_text: "",
            attributed_user_id: users[0]?.id?.toString() ?? "",
            evidence_type_ids: [],
          })
        },
      },
    )
  }

  const teamMemberNames = (report.members ?? team_members ?? [])
    .map((m) => m.user_name)
    .filter(Boolean)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Prepare Report - ${case_record.case_number}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold">Prepare Report</h1>
            <span className="rounded bg-indigo-100 px-2 py-1 text-sm text-indigo-700">
              {STATUS_LABELS[report.status] || "Unknown"}
            </span>
          </div>
          <Link
            href={`${basePath}/${report.id}`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Back to Report
          </Link>
        </div>

        {/* Team info and report details */}
        <div className="border-hpi-light-gray mb-6 rounded border bg-white p-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-hpi-med-gray font-medium">
                Team Leader:
              </span>{" "}
              {report.user_name ?? report.investigator_name ?? "-"}
            </div>
            {teamMemberNames.length > 0 && (
              <div>
                <span className="text-hpi-med-gray font-medium">
                  Team Members:
                </span>{" "}
                {teamMemberNames.join(", ")}
              </div>
            )}
            <div>
              <span className="text-hpi-med-gray font-medium">Date:</span>{" "}
              {formatDate(report.report_date)}
            </div>
            <div>
              <span className="text-hpi-med-gray font-medium">Status:</span>{" "}
              {STATUS_LABELS[report.status] || "Unknown"}
            </div>
            <div>
              <span className="text-hpi-med-gray font-medium">
                Approved by:
              </span>{" "}
              {report.approved_by_name ?? "-"}{" "}
              {report.approved_at
                ? `on ${formatDateTime(report.approved_at)}`
                : ""}
            </div>
            {report.prepared_by_name && (
              <div>
                <span className="text-hpi-med-gray font-medium">
                  Prepared by:
                </span>{" "}
                {report.prepared_by_name}{" "}
                {report.prepared_at
                  ? `on ${formatDateTime(report.prepared_at)}`
                  : ""}
              </div>
            )}
          </div>
          {report.admin_notes && (
            <div className="mt-3 border-t border-gray-200 pt-3 text-sm">
              <span className="text-hpi-med-gray font-medium">Note:</span>{" "}
              {report.admin_notes}
            </div>
          )}
        </div>

        {/* Final report inclusion banner */}
        {can_edit_entries && (
          <div
            className={`mb-6 flex items-center justify-center gap-3 rounded border p-3 ${
              report.used_in_final === 1
                ? "border-green-300 bg-green-50"
                : "border-red-300 bg-red-50"
            }`}
          >
            {report.used_in_final === 1 ? (
              <>
                <span className="font-medium text-green-700">
                  This report will be included in the Final Report
                </span>
                <button
                  onClick={handleToggleFinalInclusion}
                  className="rounded border border-red-300 bg-white px-3 py-1 text-sm text-red-700 hover:bg-red-50"
                >
                  Exclude from Final
                </button>
              </>
            ) : (
              <>
                <span className="font-medium text-red-700">
                  This report will not be included in the Final Report
                </span>
                <button
                  onClick={handleToggleFinalInclusion}
                  className="rounded border border-green-300 bg-white px-3 py-1 text-sm text-green-700 hover:bg-green-50"
                >
                  Include in Final
                </button>
              </>
            )}
          </div>
        )}

        {has_finals ? (
          <>
            <div className="border-hpi-light-gray mb-6 rounded border bg-white p-4">
              <h2 className="text-hpi-dark-gray mb-3 font-semibold">
                Prepared Entries ({final_entries.length})
              </h2>
              <div className="space-y-3">
                {final_entries.map((entry) => (
                  <div
                    key={entry.id}
                    className="border-l-2 border-blue-300 pl-3"
                  >
                    {editingEntryId === entry.id ? (
                      <form onSubmit={handleUpdateEntry} className="space-y-3">
                        <h4 className="text-sm font-semibold">Update Entry</h4>
                        <div>
                          <label
                            htmlFor={`edit_start_time_${entry.id}`}
                            className="mb-1 block text-sm font-medium"
                          >
                            Start Time
                          </label>
                          <input
                            type="datetime-local"
                            id={`edit_start_time_${entry.id}`}
                            step="1"
                            value={editForm.start_time}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                start_time: e.target.value,
                              })
                            }
                            className="border-hpi-light-gray w-full max-w-xs rounded border px-3 py-2 text-sm"
                          />
                        </div>
                        <div>
                          <label
                            htmlFor={`edit_entry_text_${entry.id}`}
                            className="mb-1 block text-sm font-medium"
                          >
                            Entry
                          </label>
                          <textarea
                            id={`edit_entry_text_${entry.id}`}
                            value={editForm.entry_text}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                entry_text: e.target.value,
                              })
                            }
                            className="border-hpi-light-gray w-full rounded border px-3 py-2 text-sm"
                            rows={6}
                            required
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-sm font-medium">
                            Evidence
                          </label>
                          <div className="flex flex-wrap gap-4">
                            {evidence_types.map((type) => (
                              <label
                                key={type.id}
                                className="flex items-center gap-1.5 text-sm"
                              >
                                <input
                                  type="checkbox"
                                  checked={editForm.evidence_type_ids.includes(
                                    type.id,
                                  )}
                                  onChange={() => toggleEditFormType(type.id)}
                                  className="rounded"
                                />
                                {type.name}
                              </label>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="submit"
                            disabled={processing}
                            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-sm text-white disabled:opacity-50"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingEntryId(null)}
                            className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2 text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    ) : (
                      <>
                        <div className="text-hpi-med-gray flex flex-wrap items-center gap-2 text-sm">
                          <span>{formatTime(entry.start_time)}</span>
                          <span>-</span>
                          <span>{entry.user_name}</span>
                          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">
                            Final
                          </span>
                          <EvidenceBadges entry={entry} />
                        </div>
                        <div className="mt-1 whitespace-pre-wrap">
                          {entry.entry_text}
                        </div>
                        <div className="mt-1 flex gap-3">
                          {can_edit_entries && (
                            <button
                              onClick={() => startEditing(entry)}
                              className="text-hpi-active-blue cursor-pointer text-xs hover:underline"
                            >
                              Edit
                            </button>
                          )}
                          {can_edit_entries && (
                            <button
                              onClick={() => {
                                if (
                                  confirm(
                                    "Are you sure you want to delete this entry?",
                                  )
                                ) {
                                  router.delete(
                                    `${basePath}/${report.id}/entries/${entry.id}`,
                                    { preserveScroll: true },
                                  )
                                }
                              }}
                              className="cursor-pointer text-xs text-red-600 hover:text-red-800 hover:underline"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {can_edit_entries && (
              <div className="flex flex-col items-center gap-3">
                {!showAddEntry ? (
                  <button
                    onClick={() => setShowAddEntry(true)}
                    className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
                  >
                    Add Entry
                  </button>
                ) : (
                  <div className="border-hpi-light-gray w-full max-w-3xl rounded border bg-white p-4">
                    <h3 className="mb-3 font-semibold">Add Entry</h3>
                    <form onSubmit={handleAddEntry}>
                      <div className="mb-3 grid gap-3 md:grid-cols-2">
                        <div>
                          <label
                            htmlFor="start_time"
                            className="mb-1 block text-sm font-medium"
                          >
                            Start Time
                          </label>
                          <input
                            type="datetime-local"
                            id="start_time"
                            step="1"
                            value={newEntry.start_time}
                            onChange={(e) =>
                              setNewEntry({
                                ...newEntry,
                                start_time: e.target.value,
                              })
                            }
                            className="border-hpi-light-gray w-full rounded border px-3 py-2 text-sm"
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="attributed_user"
                            className="mb-1 block text-sm font-medium"
                          >
                            Attribute to
                          </label>
                          <select
                            id="attributed_user"
                            value={newEntry.attributed_user_id}
                            onChange={(e) =>
                              setNewEntry({
                                ...newEntry,
                                attributed_user_id: e.target.value,
                              })
                            }
                            className="border-hpi-light-gray w-full rounded border px-3 py-2 text-sm"
                          >
                            {users.map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="mb-3">
                        <label
                          htmlFor="entry_text"
                          className="mb-1 block text-sm font-medium"
                        >
                          Entry
                        </label>
                        <textarea
                          id="entry_text"
                          value={newEntry.entry_text}
                          onChange={(e) =>
                            setNewEntry({
                              ...newEntry,
                              entry_text: e.target.value,
                            })
                          }
                          className="border-hpi-light-gray w-full rounded border px-3 py-2 text-sm"
                          rows={6}
                          required
                        />
                      </div>
                      <div className="mb-4">
                        <label className="mb-1 block text-sm font-medium">
                          Evidence
                        </label>
                        <div className="flex flex-wrap gap-4">
                          {evidence_types.map((type) => (
                            <label
                              key={type.id}
                              className="flex items-center gap-1.5 text-sm"
                            >
                              <input
                                type="checkbox"
                                checked={newEntry.evidence_type_ids.includes(
                                  type.id,
                                )}
                                onChange={() => toggleNewEntryType(type.id)}
                                className="rounded"
                              />
                              {type.name}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="submit"
                          disabled={processing}
                          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-sm text-white disabled:opacity-50"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowAddEntry(false)}
                          className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2 text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}
                <button
                  onClick={handleMarkPrepared}
                  disabled={processing}
                  className="rounded bg-indigo-600 px-6 py-3 text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {processing
                    ? "Processing..."
                    : "Done - This report is prepared and ready for the client"}
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="border-hpi-light-gray mb-6 rounded border bg-white p-4">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-hpi-dark-gray font-semibold">
                  Select Entries for Final Report
                </h2>
                <button
                  onClick={toggleAll}
                  className="text-hpi-active-blue text-sm"
                >
                  {selectedEntries.length === allEntryIds.length
                    ? "Deselect All"
                    : "Select All"}
                </button>
              </div>
              {original_entries.length > 0 ? (
                <div className="space-y-3">
                  {original_entries.map((entry) => (
                    <label
                      key={entry.id}
                      className={`border-hpi-light-gray flex cursor-pointer gap-3 rounded border p-3 ${
                        selectedEntries.includes(entry.id)
                          ? "border-indigo-300 bg-indigo-50"
                          : "bg-white"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedEntries.includes(entry.id)}
                        onChange={() => toggleEntry(entry.id)}
                        className="mt-1 h-4 w-4 rounded"
                      />
                      <div className="flex-1">
                        <div className="text-hpi-med-gray flex flex-wrap items-center gap-2 text-sm">
                          <span className="font-medium">
                            {formatTime(entry.start_time)}
                          </span>
                          <span>({entry.user_name})</span>
                          <EvidenceBadges entry={entry} />
                        </div>
                        <div className="mt-1 text-sm whitespace-pre-wrap">
                          {entry.entry_text}
                        </div>
                        {entry.also_names.length > 0 && (
                          <div className="text-hpi-med-gray mt-1 text-xs">
                            Also observed by: {entry.also_names.join(", ")}
                          </div>
                        )}
                        {entry.revisions.length > 0 && (
                          <div className="mt-2 ml-4 space-y-1">
                            <div className="text-xs font-medium text-amber-700">
                              Revisions:
                            </div>
                            {entry.revisions.map((rev) => (
                              <label
                                key={rev.id}
                                className={`flex cursor-pointer gap-2 border-l-2 pl-2 text-sm ${
                                  selectedEntries.includes(rev.id)
                                    ? "border-indigo-300 bg-indigo-50"
                                    : "border-amber-300"
                                }`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedEntries.includes(rev.id)}
                                  onChange={() => toggleEntry(rev.id)}
                                  className="mt-1 h-3 w-3 rounded"
                                />
                                <div className="flex-1">
                                  <div className="whitespace-pre-wrap">
                                    {rev.entry_text}
                                  </div>
                                  <div className="text-hpi-med-gray text-xs">
                                    {formatDate(rev.created_at)} by{" "}
                                    {rev.user_name}
                                  </div>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-hpi-med-gray text-sm">No entries found.</p>
              )}
            </div>

            {original_entries.length > 0 && (
              <div className="flex flex-col items-center gap-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={includeInFinal}
                    onChange={() => setIncludeInFinal(!includeInFinal)}
                    className="h-4 w-4 rounded"
                  />
                  Include in Final Report
                </label>
                <button
                  onClick={handleBuildPrepared}
                  disabled={processing || selectedEntries.length === 0}
                  className="rounded bg-indigo-600 px-6 py-3 text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {processing
                    ? "Building..."
                    : `Build Prepared Report (${selectedEntries.length} entries)`}
                </button>
                <p className="text-hpi-med-gray text-sm">
                  You will be able to edit the report on the next page.
                </p>
              </div>
            )}
          </>
        )}

        <div className="mt-6">
          <Link
            href={`${basePath}/${report.id}`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Back to Report
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
